<template>
  <section v-if="contextTabName.length > 0" class="py-12">
    <div class="container-wrap">
      <h2 class="text-h2 mb-6">{{ t('title') }}</h2>

      <ul
        class="scrollbar-h-px extend-width mb-6 flex w-full overflow-y-hidden overflow-x-scroll desktop:flex-wrap desktop:overflow-x-hidden"
      >
        <li
          v-for="(item, index) in contextTabName"
          :key="item"
          class="mr-2 desktop:mb-2"
        >
          <button
            class="w-full rounded-3xl border border-solid border-gray-400 px-4 py-1.5 hover:bg-blue-400 hover:text-white"
            :class="
              current === index
                ? 'bg-blue-400 text-white'
                : 'bg-white text-blue-400'
            "
            :title="item"
            @click="current = index"
          >
            <span class="truncate text-left text-sm font-bold">
              {{ item }}
            </span>
          </button>
        </li>
      </ul>

      <ul class="mb-6 last:mb-0">
        <li
          v-for="(item, index) in contextPricePlans"
          v-show="current === index"
          :key="hash(item)"
        >
          <PricePlansTable v-if="isDesktop" :price-plan-data="item" />
          <PricePlansCards v-else :price-plan-data="item" />
        </li>
      </ul>

      <div
        v-if="isShowFloatPriceNotice"
        class="flex rounded border-l-4 border-solid border-red-200 bg-red-100 p-2.5 tablet:p-4"
      >
        <div class="flex-1">
          <p class="font-bold">{{ t('floating-price') }}</p>
          <p class="text-sm desktop:text-base">
            {{ t('floating-price-notice') }}
          </p>
        </div>
        <IconCritical class="h-6 w-6 text-red-400" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import hash from 'object-hash';

  import {
    EnumBillingCycle,
    EnumServiceType,
  } from '@/assets/types/api/Products';
  import PricePlansCards from '@/components/Area/Pricing/PricePlansCards.vue';
  import PricePlansTable from '@/components/Area/Pricing/PricePlansTable.vue';
  import type { IPricePlanData } from '@/stores/pricing/pricingStore';

  const route = useRoute();
  const { t } = useI18n({ useScope: 'local' });
  const { isDesktop } = useScreen();

  const profileStore = useProfileStore();
  const pricingStore = usePricingStore();
  const { isLogin } = storeToRefs(profileStore);
  const { contextPricePlans } = storeToRefs(pricingStore);

  const current = ref<number>(0);

  const contextTabName = computed((): string[] => {
    return contextPricePlans.value.map((item) => {
      switch (item.tabId) {
        case EnumServiceType.CLOUD_ENSAAS:
          return t('cloud-ensaas');
        case EnumServiceType.CLOUD_SELF_MANAGED_SAAS:
          return t('cloud-self-managed-saas');
        case EnumServiceType.ON_PREMISES_LICENSE:
          return t('on-premises-license');
        case EnumServiceType.SOFTWARE_HARDWARE_BUNDLES:
          return t('software-hardware-bundles');
        case EnumServiceType.CONSULTING_SERVICES:
          return t('consulting-services');
        default:
          return `${item.tabId}`;
      }
    });
  });

  const contextData = computed((): IPricePlanData | null => {
    return contextPricePlans.value[current.value] || null;
  });

  const isShowFloatPriceNotice = computed((): boolean => {
    // 如果目前選到的 tab 裡面的 plan 有任何一個是 FloatingPrice 就顯示提示說明，未登入不顯示

    if (!isLogin.value) {
      return false;
    }

    if (contextData.value === null) {
      return false;
    }

    return contextData.value.plans.some((plan) => {
      const { billingCycleId, serviceType } = plan;
      return (
        billingCycleId === EnumBillingCycle.ONE_TIME_PURCHASE &&
        serviceType === EnumServiceType.LICENSE_UPGRADE
      );
    });
  });

  onMounted(() => {
    // 若 URL Hash 為 #free-trial-plan 則依 tab 順序選到第一個有免費試用方案的 tab
    if (route.hash === '#free-trial-plan') {
      const index = contextPricePlans.value.findIndex((item) =>
        item.plans.some(
          (plan) => plan.billingCycleId === EnumBillingCycle.FREE_TRIAL
        )
      );
      if (index >= 0) {
        current.value = index;
      }
    }
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "Explore Pricing Options",
    "cloud-ensaas": "Cloud (EnSaaS)",
    "cloud-self-managed-saas": "Cloud (self-managed SaaS)",
    "on-premises-license": "On-premises license",
    "software-hardware-bundles": "Software & hardware bundles",
    "consulting-services": "Consulting Services",
    "floating-price": "Floating Price",
    "floating-price-notice": "The price of this plan may vary depending on the selected items. The final amount will be directly calculated and included in the total price."
  },
  "en-jp": {
    "title": "Explore Pricing Options",
    "cloud-ensaas": "Cloud (EnSaaS)",
    "cloud-self-managed-saas": "Cloud (self-managed SaaS)",
    "on-premises-license": "On-premises license",
    "software-hardware-bundles": "Software & hardware bundles",
    "consulting-services": "Consulting Services",
    "floating-price": "Floating Price",
    "floating-price-notice": "The price of this plan may vary depending on the selected items. The final amount will be directly calculated and included in the total price."
  },
  "en-tw": {
    "title": "Explore Pricing Options",
    "cloud-ensaas": "Cloud (EnSaaS)",
    "cloud-self-managed-saas": "Cloud (self-managed SaaS)",
    "on-premises-license": "On-premises license",
    "software-hardware-bundles": "Software & hardware bundles",
    "consulting-services": "Consulting Services",
    "floating-price": "Floating Price",
    "floating-price-notice": "The price of this plan may vary depending on the selected items. The final amount will be directly calculated and included in the total price."
  },
  "en-kr": {
    "title": "Explore Pricing Options",
    "cloud-ensaas": "Cloud (EnSaaS)",
    "cloud-self-managed-saas": "Cloud (self-managed SaaS)",
    "on-premises-license": "On-premises license",
    "software-hardware-bundles": "Software & hardware bundles",
    "consulting-services": "Consulting Services",
    "floating-price": "Floating Price",
    "floating-price-notice": "The price of this plan may vary depending on the selected items. The final amount will be directly calculated and included in the total price."
  },
  "en-americas": {
    "title": "Explore Pricing Options",
    "cloud-ensaas": "Cloud (EnSaaS)",
    "cloud-self-managed-saas": "Cloud (self-managed SaaS)",
    "on-premises-license": "On-premises license",
    "software-hardware-bundles": "Software & hardware bundles",
    "consulting-services": "Consulting Services",
    "floating-price": "Floating Price",
    "floating-price-notice": "The price of this plan may vary depending on the selected items. The final amount will be directly calculated and included in the total price."
  },
  "en-asia-pacific": {
    "title": "Explore Pricing Options",
    "cloud-ensaas": "Cloud (EnSaaS)",
    "cloud-self-managed-saas": "Cloud (self-managed SaaS)",
    "on-premises-license": "On-premises license",
    "software-hardware-bundles": "Software & hardware bundles",
    "consulting-services": "Consulting Services",
    "floating-price": "Floating Price",
    "floating-price-notice": "The price of this plan may vary depending on the selected items. The final amount will be directly calculated and included in the total price."
  },
  "en-emea": {
    "title": "Explore Pricing Options",
    "cloud-ensaas": "Cloud (EnSaaS)",
    "cloud-self-managed-saas": "Cloud (self-managed SaaS)",
    "on-premises-license": "On-premises license",
    "software-hardware-bundles": "Software & hardware bundles",
    "consulting-services": "Consulting Services",
    "floating-price": "Floating Price",
    "floating-price-notice": "The price of this plan may vary depending on the selected items. The final amount will be directly calculated and included in the total price."
  },
  "ja-jp": {
    "title": "料金オプションを見る",
    "cloud-ensaas": "クラウド（EnSaaS）",
    "cloud-self-managed-saas": "クラウド（セルフマネージドSaaS）",
    "on-premises-license": "オンプレミスライセンス",
    "software-hardware-bundles": "ソフトウェア＆ハードウェアバンドル",
    "consulting-services": "コンサルティングサービス",
    "floating-price": "変動価格",
    "floating-price-notice": "このプランの料金は、選択されたアイテムによって異なる場合があります。最終的な金額は直接計算され、合計金額に含まれます。"
  },
  "zh-cn": {
    "title": "探索定价选项",
    "cloud-ensaas": "云端（EnSaaS）",
    "cloud-self-managed-saas": "云端（自主管理 SaaS）",
    "on-premises-license": "本地部署许可",
    "software-hardware-bundles": "软硬件套装",
    "consulting-services": "咨询服务",
    "floating-price": "浮动价格",
    "floating-price-notice": "价格可能因所选项目而异。最终金额将直接计算并包含在总价中。"
  },
  "zh-tw": {
    "title": "探索定價選項",
    "cloud-ensaas": "雲端（EnSaaS）",
    "cloud-self-managed-saas": "雲端（自主管理 SaaS）",
    "on-premises-license": "本地部署授權",
    "software-hardware-bundles": "軟硬體套裝",
    "consulting-services": "諮詢服務",
    "floating-price": "浮動價格",
    "floating-price-notice": "此方案價格會根據所選項目而有所變動。最終價格將直接計算並涵蓋在總價中。"
  }
}
</i18n>
